import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface WinModalProps {
  isOpen: boolean;
  onClose: () => void;
  attempts: number;
  word: string;
}

export const WinModal: React.FC<WinModalProps> = ({ isOpen, onClose, attempts, word }) => {
  useEffect(() => {
    if (isOpen) {
      // Play celebration sound if available
      // You can add sound effects here
    }
  }, [isOpen]);

  const getMessage = (attempts: number): string => {
    switch (attempts) {
      case 1:
        return 'GENIUS!';
      case 2:
        return 'MAGNIFICENT!';
      case 3:
        return 'IMPRESSIVE!';
      case 4:
        return 'SPLENDID!';
      case 5:
        return 'GREAT!';
      case 6:
        return 'PHEW!';
      default:
        return 'AMAZING!';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20,
            }}
            className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center relative overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Confetti background effect */}
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 50 }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{
                    x: Math.random() * 100 - 50,
                    y: -20,
                    rotate: 0,
                    opacity: 1,
                  }}
                  animate={{
                    x: Math.random() * 400 - 200,
                    y: 600,
                    rotate: Math.random() * 720 - 360,
                    opacity: 0,
                  }}
                  transition={{
                    duration: 3,
                    delay: Math.random() * 0.5,
                    ease: 'easeOut',
                  }}
                  className="absolute top-0 left-1/2 w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: ['#10b981', '#fbbf24', '#3b82f6', '#ec4899', '#8b5cf6'][
                      Math.floor(Math.random() * 5)
                    ],
                  }}
                />
              ))}
            </div>

            {/* Trophy Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 10,
                delay: 0.2,
              }}
              className="text-8xl mb-4"
            >
              🏆
            </motion.div>

            {/* Message */}
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-4xl font-extrabold text-green-600 mb-2"
            >
              {getMessage(attempts)}
            </motion.h2>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-gray-700 mb-6"
            >
              You guessed <span className="font-bold text-green-600">{word}</span> in{' '}
              <span className="font-bold">{attempts}</span>{' '}
              {attempts === 1 ? 'try' : 'tries'}!
            </motion.p>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-lg p-6 mb-6"
            >
              <p className="text-3xl font-bold mb-2">YOU WIN!</p>
              <p className="text-lg">No payment required!</p>
            </motion.div>

            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
            >
              Awesome!
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
